import React, { useState } from 'react';
import classNames from 'classnames';
import { Refresh, RotateClockwiseDots } from '@saleshandy/icons';
import {
  OverlayTooltip,
  Placement,
} from '../../../design-system/components/overlay';
import DataTable from '../../data-table/data-table';
import { FactorDetailsModalProps } from '../types';
import Modal from '../../../design-system/ui/modal';

const FactorDetailsModal: React.FC<FactorDetailsModalProps> = ({
  show,
  onClose,
  factorModalConfig,
  onRefresh,
  isRefreshing,
  isAccountDisconnected,
}) => {
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const handleClose = () => {
    setRefreshError(null);
    onClose();
  };

  const handleRefresh = async () => {
    if (!onRefresh) return;
    setRefreshError(null);
    try {
      await onRefresh();
    } catch {
      setRefreshError(
        'Failed to refresh. Please try again.',
      );
    }
  };

  const hasValidData =
    Array.isArray(factorModalConfig?.columns) &&
    Array.isArray(factorModalConfig?.data);

  const modalClassName = classNames([
    'factor-details-modal',
    {
      'factor-details-modal-small': factorModalConfig?.width === 'small',
    },
  ]);

  const showRefreshButton =
    !!onRefresh && !hasValidData && !factorModalConfig?.footer;

  const footerContent = factorModalConfig?.footer || (
    showRefreshButton ? (
      <div className="bs-factor-modal-footer d-flex align-items-center justify-content-end gap-8">
        {refreshError && (
          <span className="regular-1 red-txt-6 mr-auto">{refreshError}</span>
        )}
          <button
            type="button"
            className="btn btn-ghost-3"
            onClick={handleClose}
            disabled={isRefreshing}
          >
            Close
          </button>
          {isAccountDisconnected ? (
            <OverlayTooltip
              placement={Placement.Top}
              className="bs-disconnected-tooltip"
              text="Your email account is disconnected. Please reconnect it to refresh DNS records."
            >
              <span style={{ display: 'inline-block' }}>
                <button
                  type="button"
                  className="btn btn-solid-primary"
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  <Refresh width={14} height={14} />
                  <span className="ml-1">Refresh Now</span>
                </button>
              </span>
            </OverlayTooltip>
          ) : (
            <button
              type="button"
              className="btn btn-solid-primary"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <RotateClockwiseDots width={14} height={14} />
                  <span className="ml-1">Checking...</span>
                </>
              ) : (
                <>
                  <Refresh width={14} height={14} />
                  <span className="ml-1">Refresh Now</span>
                </>
              )}
            </button>
          )}
      </div>
    ) : undefined
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      title={factorModalConfig?.title}
      showCloseButton={!!factorModalConfig?.footer || hasValidData}
      className={modalClassName}
      primaryBtnLabel="Close"
      hideSecondaryBtn={true}
      hidePrimaryBtn={
        !!factorModalConfig?.footer || hasValidData || showRefreshButton
      }
      hideFooter={hasValidData && !factorModalConfig?.footer && !showRefreshButton}
      footer={footerContent}
      primaryBtnClickedHandler={handleClose}
    >
      <div>
        {factorModalConfig?.content && (
          <div className={hasValidData ? 'factor-modal-content' : ''}>
            {factorModalConfig?.content}
          </div>
        )}

        {showRefreshButton && (
          <div className="bs-factor-modal-callout mt-3">
            <div className="bs-factor-modal-callout__icon">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 5v4M8 11v1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <p className="bs-factor-modal-callout__title mb-1">
                Once you have updated your DNS settings
              </p>
              <p className="bs-factor-modal-callout__body mb-0">
                Click <strong>Refresh Now</strong> below. Saleshandy will
                recheck your record and update the status.
              </p>
            </div>
          </div>
        )}

        {hasValidData && (
          <DataTable
            columns={factorModalConfig.columns}
            data={factorModalConfig.data}
          />
        )}
      </div>
    </Modal>
  );
};

export default FactorDetailsModal;
